import type { BlockOutput, TranscriptMessage } from '../../types';
import type {
  MessagesActions,
  ServerMessageInput,
  SliceCreator,
} from './types';

// Suppress resume blocks that precede a dataTable in the same assistant turn.
// The LLM frequently fetches kula_contact_resumes for each candidate as the
// data source for a comparison present_table call. The resulting message has
// resume cards (one per candidate) THEN the comparison table — the user sees
// three full profiles before the one card that actually answers their question.
// The dataTable is the canonical answer; the resumes were data-fetch noise.
// Keep this rule in sync with the same logic in pushOutputBlock so streaming
// and history views agree.
const suppressRedundantBlocks = (outputs: BlockOutput[]): BlockOutput[] => {
  const hasDataTable = outputs.some((b) => b.type === 'dataTable');
  if (!hasDataTable) return outputs;
  return outputs.filter((b) => b.type !== 'resume');
};

// Server → store adapter. Assistant outputs prefer the api adapter's
// pre-assembled `outputs` (which folds in tool-message structured_data)
// and fall back to a plain text block when none are supplied (older
// sessions / pure-text turns). Polling returns the full transcript so
// hydration replaces, never appends.
const adaptServerMessage = (m: ServerMessageInput): TranscriptMessage => {
  if (m.role === 'user') {
    return {
      role: 'user',
      id: m.id,
      content: m.content,
      createdAt: m.createdAt,
      attachments: m.attachments,
    };
  }
  const rawOutputs =
    m.outputs && m.outputs.length > 0
      ? m.outputs
      : m.content
        ? [{ type: 'text' as const, data: { content: m.content } }]
        : [];
  return {
    role: 'assistant',
    id: m.id,
    outputs: suppressRedundantBlocks(rawOutputs),
    createdAt: m.createdAt,
    actionTrail: m.actionTrail,
  };
};

export const createMessagesSlice: SliceCreator<MessagesActions> = (set) => ({
  appendUserMessage: (sessionId, content, attachments) => {
    const id = `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    set((s) => {
      const existing = s.messagesBySession[sessionId] ?? [];
      const next: TranscriptMessage = {
        id,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
        attachments: attachments?.length ? attachments : undefined,
      };
      return {
        messagesBySession: {
          ...s.messagesBySession,
          [sessionId]: [...existing, next],
        },
      };
    });
    return id;
  },

  removeMessage: (sessionId, messageId) =>
    set((s) => {
      const existing = s.messagesBySession[sessionId];
      if (!existing?.some((message) => message.id === messageId)) return s;
      return {
        messagesBySession: {
          ...s.messagesBySession,
          [sessionId]: existing.filter((message) => message.id !== messageId),
        },
      };
    }),

  hydrateMessages: (sessionId, serverMessages) =>
    set((s) => ({
      messagesBySession: {
        ...s.messagesBySession,
        [sessionId]: serverMessages.map(adaptServerMessage),
      },
    })),

  setSessionDraft: (sessionId, text) =>
    set((s) => {
      const next = { ...s.draftBySession };
      if (text.trim().length === 0) {
        if (!(sessionId in next)) return s;
        delete next[sessionId];
      } else {
        next[sessionId] = text;
      }
      return { draftBySession: next };
    }),

  setQueuedMessage: (sessionId, turn) =>
    set((s) => {
      const existing = s.queuedBySession[sessionId] ?? [];
      // The queue id is also the logical client turn id. Re-queuing after a
      // 409/lost acknowledgement must keep its original FIFO slot instead of
      // appending a duplicate behind newer work.
      if (existing.some((queued) => queued.id === turn.id)) return s;
      return {
        queuedBySession: {
          ...s.queuedBySession,
          [sessionId]: [...existing, turn],
        },
      };
    }),

  clearQueuedMessage: (sessionId, turnId) =>
    set((s) => {
      if (!(sessionId in s.queuedBySession)) return s;
      const next = { ...s.queuedBySession };
      if (!turnId) {
        delete next[sessionId];
      } else {
        const remaining = next[sessionId].filter((turn) => turn.id !== turnId);
        if (remaining.length > 0) next[sessionId] = remaining;
        else delete next[sessionId];
      }
      return { queuedBySession: next };
    }),
});
